import { useState } from 'react';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';

const aboutBackground = 'https://readdy.ai/api/search-image?query=Warm%20and%20elegant%20modern%20office%20lounge%20with%20comfortable%20sofas%20and%20bookshelves%2C%20soft%20natural%20lighting%20through%20large%20windows%2C%20creative%20workspace%20vibe%2C%20cinematic%20atmosphere%2C%20warm%20wooden%20tones%20with%20green%20plants&width=1800&height=600&seq=about-hero-bg&orientation=landscape';

const teamImages = [
  'https://readdy.ai/api/search-image?query=Professional%20Chinese%20man%20in%20modern%20office%2C%20warm%20portrait%20photography%2C%20friendly%20expression%2C%20soft%20natural%20light%2C%20clean%20background%2C%20editorial%20style&width=400&height=400&seq=about-team-01&orientation=squarish',
  'https://readdy.ai/api/search-image?query=Professional%20Chinese%20woman%20in%20modern%20office%2C%20warm%20portrait%20photography%2C%20confident%20expression%2C%20soft%20natural%20light%2C%20clean%20background%2C%20editorial%20style&width=400&height=400&seq=about-team-02&orientation=squarish',
  'https://readdy.ai/api/search-image?query=Young%20Chinese%20man%20in%20creative%20workspace%2C%20casual%20portrait%20photography%2C%20thoughtful%20expression%2C%20warm%20ambient%20light%2C%20clean%20background%2C%20editorial%20style&width=400&height=400&seq=about-team-03&orientation=squarish',
  'https://readdy.ai/api/search-image?query=Young%20Chinese%20woman%20in%20creative%20studio%2C%20casual%20portrait%20photography%2C%20bright%20smile%2C%20warm%20natural%20light%2C%20clean%20background%2C%20editorial%20style&width=400&height=400&seq=about-team-04&orientation=squarish',
];

const teamMembers = [
  { name: '张明远', role: '创始人 & CEO', desc: '十年互联网产品经验，热爱影视文化' },
  { name: '李思雨', role: '技术总监', desc: '全栈工程师，专注流媒体技术优化' },
  { name: '王浩然', role: '内容运营', desc: '资深影评人，负责内容筛选与推荐' },
  { name: '陈小艺', role: '产品设计', desc: 'UX设计师，打造极致观影体验' },
];

const testimonials = [
  {
    name: '追剧达人小王',
    text: '黄瓜影院真的太棒了！片库超全，画质高清，关键是完全免费。我每天都在这里追剧，强烈推荐给所有喜欢看电影电视剧的朋友！',
    rating: 5,
  },
  {
    name: '影视爱好者老李',
    text: '用了很多在线影院，黄瓜影院是最让我满意的。更新快、不缓冲、资源多，智能推荐也很精准，总能发现我喜欢的好片。',
    rating: 5,
  },
  {
    name: '二次元少年阿杰',
    text: '动漫资源非常丰富，国漫日漫都有，画质也很棒。追番体验一流，还有智能推荐功能帮助发现了很多宝藏动漫作品。',
    rating: 5,
  },
];

export default function About() {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('phone_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormSubmitted(true);
      return;
    }

    const formData = new FormData(form);
    try {
      const res = await fetch('https://readdy.ai/api/form/d9nievu8mbnljin5a1a0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (res.ok && parsed?.code === 'OK') {
        setFormSubmitted(true);
        setFormError('');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setFormError('提交失败，请稍后重试。');
        } else {
          setFormError(serverMsg || '提交失败，请稍后重试。');
        }
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试。');
    }
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '黄瓜影院',
    url: 'https://huanggua.tv',
    description: '免费高清在线影视平台，提供海量电影、电视剧、动漫、综艺在线观看服务。',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'service@huanggua.tv',
      availableLanguage: ['Chinese'],
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://huanggua.tv' },
      { '@type': 'ListItem', position: 2, name: '关于我们' },
    ],
  };

  return (
    <>
      <SEOHead
        title="关于我们-品牌介绍与联系方式 | 黄瓜影院"
        description="了解黄瓜影院的品牌故事、团队介绍、用户口碑和联系方式。黄瓜影院致力于为用户提供免费高清在线影视观看服务，海量片库，流畅播放，智能推荐追剧。"
        keywords="黄瓜影院,关于我们,品牌介绍,联系方式,用户口碑,免费影视平台"
        canonicalUrl="https://huanggua.tv/about"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />

      <Navbar />

      <main className="pt-20">
        {/* Hero Banner */}
        <section className="relative w-full h-[280px] md:h-[400px] overflow-hidden">
          <img src={aboutBackground} alt="黄瓜影院团队" className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/50 via-foreground-950/30 to-background-50" />
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
            <h1 className="font-heading text-2xl md:text-4xl text-background-50 mb-3">关于黄瓜影院</h1>
            <p className="text-sm text-background-200 max-w-lg">让每一个爱追剧的人都能轻松找到好内容</p>
          </div>
        </section>

        <div className="w-full px-4 md:px-6 py-8">
          <div className="max-w-5xl mx-auto">
            <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />

            {/* Brand Introduction */}
            <section className="mt-8 mb-14">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-900 mb-5">品牌介绍</h2>
              <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 md:p-8">
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  <strong>黄瓜影院</strong>成立于2020年，是一家专注于<strong>免费在线影视</strong>服务的互联网平台。我们的使命是让每一位用户都能轻松享受到高质量的影视内容，无需付费，无需等待。
                </p>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  在内容方面，黄瓜影院拥有<strong>海量片库</strong>，涵盖电影、电视剧、动漫、综艺等各类内容。我们与多家内容提供商合作，持续更新和扩充片源，确保用户始终能找到想看的内容。无论是<strong>最新剧集完整版</strong>还是经典老片，黄瓜影院都能满足您的需求。
                </p>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  技术方面，黄瓜影院采用先进的<strong>CDN加速</strong>和智能转码技术，确保<strong>高清画质即点即播</strong>，实现<strong>流畅播放不缓冲</strong>的观影体验。同时，我们的<strong>智能推荐系统</strong>能根据用户的观影偏好，精准推荐感兴趣的内容，帮助用户<strong>快速找片</strong>，轻松<strong>追剧</strong>。
                </p>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                  未来，黄瓜影院将继续坚持"<strong>免费、高清、智能</strong>"的核心理念，不断优化产品体验，为更多用户带来优质的在线观影服务。让每一次观影都成为享受，让每一个好故事都不被错过。
                </p>
              </div>
            </section>

            {/* Team */}
            <section className="mb-14">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-900 mb-5">核心团队</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
                {teamMembers.map((member, idx) => (
                  <div
                    key={idx}
                    className="bg-background-50 border border-background-200/70 rounded-xl p-5 text-center hover:border-primary-200 transition-all duration-200"
                  >
                    <div className="w-20 h-20 mx-auto mb-3 rounded-full overflow-hidden bg-background-200">
                      <img
                        src={teamImages[idx]}
                        alt={member.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">{member.name}</h3>
                    <p className="text-xs text-primary-600 font-medium mb-2">{member.role}</p>
                    <p className="text-xs text-foreground-500 leading-snug">{member.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Testimonials */}
            <section className="mb-14">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-900 mb-5">用户口碑</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {testimonials.map((t, idx) => (
                  <div
                    key={idx}
                    className="bg-background-50 border border-background-200/70 rounded-xl p-6"
                  >
                    <div className="flex gap-0.5 mb-3">
                      {Array.from({ length: t.rating }).map((_, i) => (
                        <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed mb-4">{t.text}</p>
                    <p className="text-xs text-foreground-500 font-medium">—— {t.name}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Contact */}
            <section className="mb-14">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-900 mb-5">联系我们</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 md:p-8">
                  <h3 className="text-base font-semibold text-foreground-900 mb-4">联系方式</h3>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                        <i className="ri-mail-line text-lg"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-800">电子邮箱</p>
                        <p className="text-sm text-foreground-500">service@huanggua.tv</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                        <i className="ri-phone-line text-lg"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-800">客服电话</p>
                        <p className="text-sm text-foreground-500">400-888-6666</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                        <i className="ri-map-pin-line text-lg"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-800">公司地址</p>
                        <p className="text-sm text-foreground-500">北京市朝阳区望京科技园A座</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 md:p-8">
                  <h3 className="text-base font-semibold text-foreground-900 mb-4">发送消息</h3>
                  {formSubmitted ? (
                    <div className="flex flex-col items-center justify-center py-8 text-center">
                      <div className="w-14 h-14 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-4">
                        <i className="ri-check-line text-2xl"></i>
                      </div>
                      <p className="text-sm font-semibold text-foreground-800 mb-1">消息已发送！</p>
                      <p className="text-xs text-foreground-500">感谢您的留言，我们会尽快回复。</p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-4" data-readdy-form id="about-contact-form">
                      <div>
                        <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-700 mb-1.5">姓名</label>
                        <input
                          id="contact-name"
                          name="name"
                          type="text"
                          required
                          className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-all"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-700 mb-1.5">邮箱</label>
                        <input
                          id="contact-email"
                          name="email"
                          type="email"
                          required
                          className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-all"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-700 mb-1.5">留言内容</label>
                        <textarea
                          id="contact-message"
                          name="message"
                          required
                          maxLength={500}
                          rows={4}
                          className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300 transition-all resize-none"
                          placeholder="请输入您的留言（最多500字）"
                        ></textarea>
                      </div>
                      <div className="hp-field">
                        <label htmlFor="hp-alt">备用电话</label>
                        <input id="hp-alt" name="phone_alt" type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                      </div>
                      {formError && (
                        <p className="text-xs text-primary-600">{formError}</p>
                      )}
                      <button
                        type="submit"
                        className="w-full px-6 py-3 text-sm font-semibold bg-primary-500 text-background-50 rounded-lg hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        发送消息
                      </button>
                    </form>
                  )}
                </div>
              </div>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}